import React, { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import api from "../../services/ApiService";
import { useAuth } from "../../contexts/AuthContext";

interface TermsModalProps {
  isOpen: boolean;
  onAccept: () => void;
  onClose: () => void;
}

const TermsModal: React.FC<TermsModalProps> = ({ isOpen, onAccept, onClose }) => {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);

  if (!isOpen) return null;

  const handleAccept = async () => {
    setIsLoading(true);
    try {
      await api.acceptTerms();
      onAccept();
    } catch (error) {
      console.error("Failed to accept terms:", error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: "rgba(0, 0, 0, 0.7)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 9999,
      }}
    >
      <div
        style={{
          backgroundColor: "white",
          borderRadius: "12px",
          padding: "32px",
          maxWidth: "600px",
          maxHeight: "80vh",
          overflowY: "auto",
          margin: "16px",
          boxShadow: "0 4px 20px rgba(0, 0, 0, 0.15)",
        }}
      >
        <h2 style={{ marginBottom: "16px", fontSize: "24px", fontWeight: 600 }}>
          {t("terms_title", "ИНФОРМАЦИОННЫЙ СЕРВИС \"DISPATCHER\"")}
        </h2>

        <div style={{ marginBottom: "24px", lineHeight: 1.6, color: "#333" }}>
          <p style={{ marginBottom: "12px" }}>
            <strong>Соглашение об отказе от ответственности</strong>
          </p>

          <p style={{ marginBottom: "12px" }}>
            Настоящий сервис является информационной платформой, которая предоставляет 
            возможность размещения и поиска объявлений об услугах. Мы выступаем исключительно 
            в качестве посредника между заказчиком и исполнителем.
          </p>

          <p style={{ marginBottom: "8px" }}>
            <strong>КОМИССИИ И ПЛАТЕЖИ:</strong>
          </p>
          <ul style={{ paddingLeft: "20px", marginBottom: "12px" }}>
            <li>Комиссия сервиса составляет <strong>15%</strong> от суммы заказа</li>
            <li>Комиссия эквайринга (платёжного агрегатора) составляет <strong>2.5%</strong> от суммы заказа</li>
            <li>Исполнитель получает сумму заказа за вычетом комиссии сервиса и комиссии эквайринга</li>
            <li>В карточке заказа и на карте отображается сумма, которую исполнитель получит «на руки» (чистая сумма к выплате)</li>
            <li>Проценты комиссий могут быть изменены администратором сервиса</li>
          </ul>

          <p style={{ marginBottom: "8px" }}>
            <strong>ОТКАЗ ОТ ОТВЕТСТВЕННОСТИ:</strong>
          </p>
          <ol style={{ paddingLeft: "20px", marginBottom: "12px" }}>
            <li>Сервис не является стороной сделок между пользователями и не несет ответственности за качество, своевременность и результаты выполненных работ.</li>
            <li>Мы не проводим проверку квалификации, документов, лицензий или разрешений исполнителей.</li>
            <li>Все риски, связанные с выполнением заказов, включая финансовые, юридические и иные, несут исключительно стороны сделки.</li>
            <li>Мы не гарантируем безопасность сделок и не несем ответственности за возможные убытки.</li>
            <li>Пользователи самостоятельно несут ответственность за проверку информации о контрагенте.</li>
          </ol>

          <p style={{ marginBottom: "12px" }}>
            <strong>ИСПОЛЬЗУЯ СЕРВИС, ВЫ ПОДТВЕРЖДАЕТЕ, ЧТО:</strong>
          </p>
          <ul style={{ paddingLeft: "20px" }}>
            <li>Ознакомлены с настоящим соглашением</li>
            <li>Принимаете на себя все риски, связанные с использованием сервиса</li>
            <li>Несёте полную ответственность за свои действия как заказчик или исполнитель</li>
          </ul>
        </div>

        <div style={{ display: "flex", gap: "12px", justifyContent: "center" }}>
          <button
            onClick={onClose}
            style={{
              padding: "12px 24px",
              borderRadius: "8px",
              border: "1px solid #ccc",
              backgroundColor: "#f5f5f5",
              cursor: "pointer",
              fontSize: "16px",
            }}
          >
            {t("decline", "Decline")}
          </button>
          <button
            onClick={handleAccept}
            disabled={isLoading}
            style={{
              padding: "12px 24px",
              borderRadius: "8px",
              border: "none",
              backgroundColor: "#1976d2",
              color: "white",
              cursor: isLoading ? "not-allowed" : "pointer",
              fontSize: "16px",
              opacity: isLoading ? 0.7 : 1,
            }}
          >
            {isLoading ? t("loading", "Загрузка...") : t("accept", "Принять")}
          </button>
        </div>
      </div>
    </div>
  );
};

export default TermsModal;

// Проверка требований онбординга. При невыполненных требованиях backend
// возвращает 403 с `error.metadata.missing` (`terms` / `name` / `phone`).
// Порядок обязателен: сначала условия (блокирующий модальный экран), затем
// профиль (блокирующий редирект, см. RequirementsGate).
export type RequirementsStatus = "pending" | "ok" | "error";

// Задержки между авто-попытками: 1с, 2с, 4с. После исчерпания показывается
// экран ручного повтора.
const REQUIREMENTS_RETRY_DELAYS_MS = [1000, 2000, 4000];

export const useTermsCheck = () => {
  const { isAuthenticated, user, logout } = useAuth();
  const { pathname } = useLocation();
  const [missing, setMissing] = useState<string[]>([]);
  const [termsOpen, setTermsOpen] = useState(false);
  // Три состояния запроса требований:
  // - `pending` — ответа ещё нет (идёт запрос или авто-ретраи);
  // - `ok`      — сервер ответил, `missing` достоверен;
  // - `error`   — все попытки исчерпаны, требования подтвердить не удалось.
  // Пустой `missing` при `pending`/`error` НЕ означает, что требования
  // выполнены, поэтому гейт обязан различать эти состояния (fail-closed).
  const [status, setStatus] = useState<RequirementsStatus>("pending");
  // Инкремент запускает повторную загрузку по кнопке «Повторить».
  const [reloadToken, setReloadToken] = useState(0);

  useEffect(() => {
    if (!isAuthenticated) {
      setMissing([]);
      setStatus("pending");
      return;
    }
    // Смена пользователя — прежние требования больше не действительны.
    setMissing([]);
    setStatus("pending");
  }, [isAuthenticated, user?.id]);

  useEffect(() => {
    if (!isAuthenticated) return;
    let alive = true;
    let timer: ReturnType<typeof setTimeout> | undefined;

    const attempt = (index: number) => {
      api
        .checkUserRequirements()
        .then((res) => {
          if (!alive) return;
          setMissing(res.ok ? [] : res.missing ?? []);
          setStatus("ok");
        })
        .catch(() => {
          if (!alive) return;
          if (index < REQUIREMENTS_RETRY_DELAYS_MS.length) {
            timer = setTimeout(
              () => attempt(index + 1),
              REQUIREMENTS_RETRY_DELAYS_MS[index],
            );
            return;
          }
          // Fail-closed: подтвердить требования не удалось — доступ не открываем.
          // Уже подтверждённого пользователя не понижаем из `ok`, чтобы фоновая
          // перепроверка при навигации не выкидывала его из приложения.
          setStatus((prev) => (prev === "ok" ? prev : "error"));
        });
    };

    attempt(0);

    return () => {
      alive = false;
      if (timer) clearTimeout(timer);
    };
  }, [isAuthenticated, pathname, user?.id, reloadToken]);

  const termsMissing = missing.includes("terms");

  // Условия нельзя пропустить: пока они не приняты, модальный экран показывается
  // автоматически, а не по кнопке из напоминания.
  useEffect(() => {
    if (termsMissing) setTermsOpen(true);
  }, [termsMissing]);

  // Вызывается после успешного приёма условий (без перезагрузки страницы)
  const handleTermsAccepted = () => {
    setMissing((m) => m.filter((x) => x !== "terms"));
    setTermsOpen(false);
  };

  // Отказ от условий равносилен выходу из аккаунта — сервис не может быть
  // использован без их принятия. Так же ведёт себя мобильный клиент.
  const handleTermsDecline = () => {
    setTermsOpen(false);
    logout();
  };

  // Ручной повтор после исчерпания авто-ретраев.
  const retry = useCallback(() => {
    setStatus("pending");
    setReloadToken((token) => token + 1);
  }, []);

  return {
    missing,
    status,
    termsMissing,
    termsOpen,
    retry,
    handleTermsAccepted,
    handleTermsDecline,
  };
};